import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  FaArrowRight, FaHandshake, FaRoute, FaUsers, FaHeart, FaSeedling, FaComments,
} from 'react-icons/fa';
import './JoinMc.css';
import Footer from '../Footer';

const MC_FORM = 'https://thecitychurchluzira.churchcenter.com/people/forms/938187';

const PATHS = [
  {
    icon: <FaHandshake aria-hidden="true" />,
    tag: 'Step 1',
    title: 'First-time guests',
    desc: 'New to The City Church? Let us know you visited so we can welcome you properly and stay connected.',
    cta: 'Fill the connection card',
    to: '/ConnectionCardLin',
    internal: true,
  },
  {
    icon: <FaRoute aria-hidden="true" />,
    tag: 'Step 2',
    title: 'Next Steps classes',
    desc: 'Nine short classes that lay the foundations of faith and help you find your place in the family.',
    cta: 'Explore Next Steps',
    to: '/nextSteps',
    internal: true,
  },
  {
    icon: <FaUsers aria-hidden="true" />,
    tag: 'Step 3',
    title: 'Join a Missional Community',
    desc: 'Small groups of believers who gather to grow in faith, serve the community and share the love of Christ.',
    cta: 'Join a community',
    to: MC_FORM,
    internal: false,
  },
];

const WHY = [
  { icon: <FaHeart aria-hidden="true" />, title: 'Belong', desc: 'Be known by name in a community that shows up for you.' },
  { icon: <FaSeedling aria-hidden="true" />, title: 'Grow', desc: 'Mature in your faith alongside people on the same journey.' },
  { icon: <FaComments aria-hidden="true" />, title: 'Serve', desc: 'Use your gifts to make a real difference where you live.' },
];

function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const root = ref.current;
    if (!root) return undefined;
    const els = root.querySelectorAll('.reveal');
    if (!('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('is-visible'));
      return undefined;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add('is-visible'); io.unobserve(e.target); }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
  return ref;
}

function JoinMc() {
  const ref = useReveal();

  return (
    <div ref={ref}>
      {/* HERO */}
      <header className="jm-hero">
        <div className="tcc-container jm-hero__inner">
          <p className="tcc-eyebrow jm-hero__eyebrow">Get connected</p>
          <h1 className="jm-hero__title">Find your place with us</h1>
          <p className="jm-hero__lead">
            Whether it is your very first Sunday or you are ready to go deeper, there is a
            simple next step for you. Here is how to get connected at The City Church.
          </p>
          <div className="jm-hero__actions">
            <a className="tcc-btn tcc-btn--accent tcc-btn--lg" href="#jm-paths">
              See the ways to connect <FaArrowRight aria-hidden="true" />
            </a>
            <a className="tcc-btn tcc-btn--outline-on-dark tcc-btn--lg" href={MC_FORM} target="_blank" rel="noopener noreferrer">
              Join a community
            </a>
          </div>
        </div>
      </header>

      {/* PATHS */}
      <section id="jm-paths" className="tcc-section jm-paths reveal" aria-labelledby="jm-paths-h" style={{ scrollMarginTop: '80px' }}>
        <div className="tcc-container">
          <div className="jm-head">
            <p className="tcc-eyebrow">Three simple steps</p>
            <h2 className="tcc-h2" id="jm-paths-h">Ways to get connected</h2>
            <p className="tcc-lead">Take them in order, or jump straight to the one that fits where you are.</p>
          </div>
          <ul className="jm-grid">
            {PATHS.map((p) => (
              <li className="jm-card tcc-card tcc-card--hover" key={p.title}>
                <span className="jm-card__icon">{p.icon}</span>
                <span className="jm-card__tag">{p.tag}</span>
                <h3 className="jm-card__title">{p.title}</h3>
                <p className="jm-card__desc">{p.desc}</p>
                {p.internal ? (
                  <Link className="jm-card__cta" to={p.to}>
                    {p.cta} <FaArrowRight aria-hidden="true" />
                  </Link>
                ) : (
                  <a className="jm-card__cta" href={p.to} target="_blank" rel="noopener noreferrer">
                    {p.cta} <FaArrowRight aria-hidden="true" />
                  </a>
                )}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* WHY */}
      <section className="tcc-section tcc-section--surface2 jm-why reveal" aria-labelledby="jm-why-h">
        <div className="tcc-container">
          <div className="jm-head">
            <p className="tcc-eyebrow">Why community</p>
            <h2 className="tcc-h2" id="jm-why-h">You were never meant to do it alone</h2>
          </div>
          <ul className="jm-why__grid">
            {WHY.map((w) => (
              <li className="jm-why__card tcc-card tcc-card--hover" key={w.title}>
                <span className="jm-why__icon">{w.icon}</span>
                <h3 className="jm-why__title">{w.title}</h3>
                <p className="jm-why__desc">{w.desc}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section className="jm-cta reveal" aria-labelledby="jm-cta-h">
        <div className="tcc-container jm-cta__inner">
          <h2 className="jm-cta__title" id="jm-cta-h">Ready to take the next step?</h2>
          <p className="jm-cta__text">
            Join a Missional Community today and start doing life with people who will
            cheer you on, pray with you and grow alongside you.
          </p>
          <div className="jm-cta__actions">
            <a className="tcc-btn tcc-btn--on-dark tcc-btn--lg" href={MC_FORM} target="_blank" rel="noopener noreferrer">
              Join a Missional Community <FaArrowRight aria-hidden="true" />
            </a>
            <Link className="tcc-btn tcc-btn--outline-on-dark tcc-btn--lg" to="/contact-us">
              Talk to our team
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default JoinMc;
